import {
  ChoiceGroup,
  CommandBar,
  DefaultButton,
  DetailsListLayoutMode,
  Dropdown,
  FontIcon,
  ICommandBarItemProps,
  IDropdownOption,
  IconButton,
  IStackTokens,
  mergeStyles,
  mergeStyleSets,
  PrimaryButton,
  Separator,
  ShimmeredDetailsList,
  Stack,
  Text,
  TextField,
  TooltipHost,
} from '@fluentui/react';
import { Dialog, DialogFooter, DialogType } from '@fluentui/react/lib/Dialog';
import { useEffect, useMemo, useState } from 'react';
import {
  getImportAssets,
  IImportedAsset,
  IImportResult,
  ILastImport,
  importAssets,
  importAssetsAndLink,
  previewImportAssets,
} from '../../services/importService';
import { downloadFile } from '../../common/DownloadFile';
import ImportLogs from './ImportLogs';
import { useSetAtom } from 'jotai';
import { isInProgressAtom } from '../../atoms/messageBarAtoms';
import { RenderIf } from '../../libs';
import { FileSelector } from '../../common/FileSelector';
import { getEntitiesMetadatas } from '../../services/metadataService';
import { formatDate } from '@moko/utils';

interface IImportAssetProps {
  onAssetsImported?: (importedAssets: IImportedAsset[]) => void | Promise<void>;
  linkEntity?: { entityType: 'Order' | 'Work'; entityId: number };
}

const ImportAsset = ({ onAssetsImported, linkEntity }: IImportAssetProps) => {
  const importDialogContentProps = {
    type: DialogType.normal,
    title: 'Initiating import',
    closeButtonAriaLabel: 'Close',
  };
  const setIsInProgress = useSetAtom(isInProgressAtom);

  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isImportFileDialogOpen, setIsImportFileDialogOpen] = useState(false);

  const [message, setMessage] = useState('');

  const [importedAssets, setImportedAssets] = useState<IImportedAsset[]>([]);

  const [shimmered, setShimmered] = useState(false);
  const [showImportedDataList, setShowImportedDataList] = useState(false);
  const [logFile, setLogFile] = useState('');

  const [importData, setImportData] = useState({
    lastImport: { lastImportedDateTime: '', lastImportedId: '', createdAt: '' },
    latestImportedAsset: { code: '', changeDate: '' },
    latestAssetInSourceDate: '',
    latestAssetInSourceNum: '',
  });

  useEffect(() => {
    const abortController = new AbortController();

    setIsInProgress(true);
    setMessage('Fetching import history');
    getImportAssets(abortController)
      .then((data: ILastImport) => {
        setMessage('');
        setImportData(data);
        setIsInProgress(false);
      })
      .catch((error) => {
        console.error('Error:', error);
        setMessage(error.Message);
        setIsInProgress(false);
      });

    return () => {
      abortController.abort();
    };
  }, [importData?.latestAssetInSourceDate]);

  const stackTokens: IStackTokens = {
    childrenGap: 5,
    padding: 10,
  };

  const handlePreviewImportClick = () => {
    setShowImportedDataList(true);
    setShimmered(true);
    const abortController = new AbortController();

    previewImportAssets(abortController)
      .then((data: IImportedAsset[]) => {
        const message = `${data.length} assets to be imported`;
        setMessage(message);
        setImportedAssets(data);
        setShimmered(false);
      })
      .catch((error) => {
        console.error('Error:', error);
        setShowImportedDataList(false);
        setMessage(error.Message);
      });
  };

  const handleImportClick = () => {
    setShowImportedDataList(true);
    setShimmered(true);
    const abortController = new AbortController();

    const runImport = linkEntity
      ? importAssetsAndLink(
          abortController,
          linkEntity.entityType,
          linkEntity.entityId,
          clearChoice,
          seed
        )
      : importAssets(abortController, clearChoice, seed);
    runImport
      .then(async (data: IImportResult) => {
        const message = `${data.importedItems.length} items imported`;
        setMessage(message);
        setImportedAssets(data.importedItems);
        setLogFile(data.logFilePath);
        setShimmered(false);
        if (onAssetsImported) {
          try {
            await onAssetsImported(data.importedItems);
          } catch (error) {
            console.error('Error linking imported assets:', error);
          }
        }
      })
      .catch((error) => {
        console.error('Error:', error);
        setShowImportedDataList(false);
        setMessage(error.Message);
      });
    setIsImportDialogOpen(false);
  };

  const handleImportFileClick = () => {
    setShowImportedDataList(true);
    setShimmered(true);
    const abortController = new AbortController();

    const runImport = linkEntity
      ? importAssetsAndLink(
          abortController,
          linkEntity.entityType,
          linkEntity.entityId,
          clearChoice,
          seed,
          csvContent,
          selectedAssetTypeKey?.split('|')[1]
        )
      : importAssets(abortController, clearChoice, seed, csvContent);
    runImport
      .then(async (data: IImportResult) => {
        const message = `${data.importedItems.length} items imported`;
        setMessage(message);
        setImportedAssets(data.importedItems);
        setLogFile(data.logFilePath);
        setShimmered(false);
        if (onAssetsImported) {
          try {
            await onAssetsImported(data.importedItems);
          } catch (error) {
            console.error('Error linking imported assets:', error);
          }
        }
      })
      .catch((error) => {
        console.error('Error:', error);
        setShowImportedDataList(false);
        setMessage(error.Message);
      });
    setIsImportFileDialogOpen(false);
  };

  const columns = [
    {
      key: 'Id',
      name: 'Id',
      fieldName: 'id',
      minWidth: 25,
      maxWidth: 50,
      isResizable: true,
    },
    {
      key: 'Code',
      name: 'Code',
      fieldName: 'code',
      minWidth: 150,
      maxWidth: 250,
      isResizable: true,
    },
    {
      key: 'Date',
      name: 'Date',
      fieldName: 'changedDate',
      minWidth: 150,
      maxWidth: 175,
      isResizable: true,
    },
  ];

  const [clearChoice, setClearChoice] = useState(false);
  const [seed, setSeed] = useState(1);

  const optionRootClass = mergeStyles({
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
  });
  const warningIconClass = mergeStyles({
    fontSize: 20,
    height: 20,
    width: 20,
    margin: '0 10px',
    color: 'red',
  });
  const classNames = mergeStyleSets({
    dataListContainer: {
      height: 500,
      overflow: 'auto',
    },
  });

  const commandBarItems: ICommandBarItemProps[] = [
    {
      key: 'preview',
      iconProps: { iconName: 'Blur' },
      text: 'Preview import',
      onClick: () => handlePreviewImportClick(),
    },
    {
      key: 'start',
      iconProps: { iconName: 'Play' },
      text: 'Start import',
      subMenuProps: {
        items: [
          {
            key: 'startFromApi',
            text: 'Import from Api',
            iconProps: { iconName: 'AzureAPIManagement' },
            onClick: () => setIsImportDialogOpen(true),
          },
          {
            key: 'startFromFile',
            text: 'Import from CSV',
            iconProps: { iconName: 'ExcelDocument' },
            onClick: () => setIsImportFileDialogOpen(true),
          },
        ],
      },
    },
    {
      key: 'logFile',
      text: 'Show Log File',
      iconProps: { iconName: 'DownloadDocument' },
      disabled: !logFile,
      onClick: () => {
        try {
          setIsInProgress(true);
          downloadFile(logFile);
        } finally {
          setIsInProgress(false);
        }
      },
    },
  ];

  const [csvContent, setCsvContent] = useState('');
  const [firstLine, setFirstLine] = useState<string>();
  const [csvValidation, setCsvValidation] = useState<string>();
  const [assetTypeOptions, setAssetTypeOptions] = useState<IDropdownOption[]>(
    []
  );
  const [selectedAssetTypeKey, setSelectedAssetTypeKey] = useState<
    string | undefined
  >();
  const [assetTypeColumnsMap, setAssetTypeColumnsMap] = useState<
    Record<string, string[]>
  >({});

  const mainColumns: string = 'Code,Status,Name,ChangeDate,Zone,Category,Asset';
  const supportedMetadataFieldTypes = [
    'String',
    'Number',
    'AutoComplete',
    'Lookup',
    'Checkbox',
    'RadioButton',
  ];

  useEffect(() => {
    const abortController = new AbortController();

    getEntitiesMetadatas(abortController, ['Asset'])
      .then((entityMetadatas) => {
        const metadatas = entityMetadatas?.metadatas ?? {};
        const options: IDropdownOption[] = [];
        const columnsMap: Record<string, string[]> = {};

        Object.entries(metadatas).forEach(([metadataKey, metadataValue]) => {
          if (!metadataKey.startsWith('Asset|')) {
            return;
          }

          const metadataName = metadataKey.split('|')[1] ?? metadataKey;
          const fields = metadataValue.fields ?? metadataValue.metadata?.fields;
          const metadataColumns = (fields ?? [])
            .filter((field) =>
              supportedMetadataFieldTypes.includes(field.fieldType)
            )
            .map((field) => field.name?.trim())
            .filter((fieldName): fieldName is string => !!fieldName);

          options.push({ key: metadataKey, text: metadataName });
          columnsMap[metadataKey] = metadataColumns;
        });

        options.sort((a, b) =>
          String(a.text).localeCompare(String(b.text), undefined, {
            sensitivity: 'base',
          })
        );

        setAssetTypeOptions(options);
        setAssetTypeColumnsMap(columnsMap);
        setSelectedAssetTypeKey((previousKey) => {
          if (previousKey && columnsMap[previousKey]) {
            return previousKey;
          }
          return options[0]?.key?.toString();
        });
      })
      .catch((error) => {
        console.error('Error:', error);
      });

    return () => {
      abortController.abort();
    };
  }, []);

  const columnsList = useMemo(() => {
    const selectedMetadataColumns = selectedAssetTypeKey
      ? assetTypeColumnsMap[selectedAssetTypeKey] ?? []
      : [];

    return [
      ...new Set([
        ...mainColumns
          .split(',')
          .map((column) => column.trim())
          .filter((column) => !!column),
        ...selectedMetadataColumns,
      ]),
    ].join(',');
  }, [assetTypeColumnsMap, mainColumns, selectedAssetTypeKey]);

  const formatTemplateTimestamp = (date: Date): string => {
    const pad = (value: number): string => value.toString().padStart(2, '0');
    return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(
      date.getDate()
    )}-${pad(date.getHours())}${pad(date.getMinutes())}`;
  };

  const handleDownloadHeaderTemplate = () => {
    const fileName = `template(${formatTemplateTimestamp(new Date())}).csv`;
    const csvTemplate = `${columnsList}\n`;
    const blob = new Blob([csvTemplate], {
      type: 'text/csv;charset=utf-8;',
    });
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(objectUrl);
  };

  const findFirstDiff = (input: string): number => {
    for (let index = 0; index < input.length; index++) {
      if (columnsList[index] !== input[index]) {
        return index;
      }
    }
    return 0;
  };
  return (
    <>
      <Dialog
        hidden={!isImportDialogOpen}
        onDismiss={() => setIsImportDialogOpen(false)}
        dialogContentProps={importDialogContentProps}
      >
        <Stack horizontal>
          <ChoiceGroup
            label="Clear existing assets"
            defaultSelectedKey="no"
            options={[
              {
                key: 'yes',
                text: 'Yes',
                styles: { root: { border: '1px solid red' } },
                onRenderField: (props, render) => {
                  return (
                    <div className={optionRootClass}>
                      {render!(props)}
                      <FontIcon
                        aria-label="Train"
                        iconName="WarningSolid"
                        className={warningIconClass}
                      />
                    </div>
                  );
                },
              },
              { key: 'no', text: 'No' },
            ]}
            onChange={(_ev, option) => {
              setClearChoice(option?.key === 'yes');
            }}
          />
        </Stack>
        <TextField
          label="Starting number"
          disabled={!clearChoice}
          onChange={(_ev, newValue) => {
            if (newValue) {
              setSeed(parseInt(newValue));
            }
          }}
        />

        <DialogFooter>
          <PrimaryButton onClick={() => handleImportClick()} text="Yes" />
          <DefaultButton
            onClick={() => setIsImportDialogOpen(false)}
            text="No"
          />
        </DialogFooter>
      </Dialog>

      <Dialog
        hidden={!isImportFileDialogOpen}
        onDismiss={() => setIsImportFileDialogOpen(false)}
        dialogContentProps={importDialogContentProps}
      >
        <Stack>
          <Dropdown
            label="Asset type"
            options={assetTypeOptions}
            selectedKey={selectedAssetTypeKey}
            onChange={(_ev, option) =>
              setSelectedAssetTypeKey(option?.key?.toString())
            }
            disabled={assetTypeOptions.length === 0}
            placeholder={
              assetTypeOptions.length > 0
                ? 'Select asset type'
                : 'No asset type found'
            }
          />
          <Text variant="medium">
            Please select a CSV file with following columns:
          </Text>
          <span
            style={{
              borderStyle: 'solid',
              borderWidth: 1,
              borderColor: 'black',
              borderBottomStyle: 'none',
              boxShadow: '2px 5px 2px gray',
              height: 55,
              width: 310,
              marginLeft: -10,
              marginTop: 8,
              display: 'inline-block',
              overflowX: 'auto',
            }}
          >
            <Text
              variant="tiny"
              style={{ fontFamily: 'monospace' }}
              id="AssetHeader"
            >
              &nbsp;{columnsList}
            </Text>
          </span>
          <Stack horizontal horizontalAlign="end" style={{ width: '100%' }}>
            <TooltipHost content="Copy to clipboard">
              <IconButton
                iconProps={{ iconName: 'Copy' }}
                style={{ marginTop: 5 }}
                onClick={() => {
                  navigator.clipboard.writeText(columnsList);
                }}
              />
            </TooltipHost>
            <TooltipHost content="Download empty CSV header template">
              <IconButton
                iconProps={{ iconName: 'Download' }}
                style={{ marginTop: 5 }}
                onClick={handleDownloadHeaderTemplate}
              />
            </TooltipHost>
          </Stack>
          <br />
          <Text variant="small">
            * Company specific fields can follow these columns.
          </Text>
          <Text variant="small">
            ** If Zone or Category does not match with existing code they would
            be created.
          </Text>
          <br />
          <FileSelector
            value={''}
            uploadImage={false}
            isTextFile={true}
            purpose="ImportAsset"
            onFileSelected={(_name, content) => {
              const contentStr = content as string;
              const lines = contentStr.split('\n');
              if (lines.length <= 1) {
                setCsvValidation('Error. Empty file or not correct line break');
                return;
              }
              const firstLine = lines[0];
              setCsvContent(contentStr);
              if (firstLine?.trimEnd().endsWith(',')) {
                setCsvValidation('Error. Headers should not end with ,');
              } else if (
                firstLine
                  ?.replace(/\s+/g, '')
                  .toLowerCase()
                  .startsWith(columnsList.replace(/\s+/g, '').toLowerCase())
              ) {
                setCsvValidation('ok: Headers match');
              } else {
                setCsvValidation(`Error. The columns doesn't match`);
              }
              setFirstLine(firstLine);
            }}
          />
          <RenderIf condition={!!csvValidation?.startsWith('Error')}>
            <span
              style={{
                borderStyle: 'solid',
                borderColor: 'red',
                borderBottomStyle: 'none',
                boxShadow: '2px 5px 2px gray',
                height: 55,
              }}
            >
              <p
                style={{ fontFamily: 'monospace', fontSize: 10, color: 'red' }}
              >
                {firstLine}
              </p>
              <p
                style={{ fontFamily: 'monospace', fontSize: 10, color: 'red' }}
              >{`${'-'.repeat(findFirstDiff(firstLine ?? ''))}^`}</p>
            </span>
          </RenderIf>
          <RenderIf condition={csvValidation !== undefined}>
            <Text
              variant="medium"
              styles={{
                root: {
                  color: csvValidation?.startsWith('ok') ? 'green' : 'red',
                },
              }}
            >
              {csvValidation}
            </Text>
          </RenderIf>
        </Stack>
        <Stack horizontal>
          <ChoiceGroup
            label="Clear existing assets"
            defaultSelectedKey="no"
            options={[
              {
                key: 'yes',
                text: 'Yes',
                styles: { root: { color: 'red' } },
                onRenderField: (props, render) => {
                  return (
                    <div className={optionRootClass}>
                      {render!(props)}
                      <FontIcon
                        aria-label="Train"
                        iconName="WarningSolid"
                        className={warningIconClass}
                      />
                    </div>
                  );
                },
              },
              { key: 'no', text: 'No' },
            ]}
            onChange={(_ev, option) => {
              setClearChoice(option?.key === 'yes');
            }}
          />
        </Stack>
        <TextField
          label="Starting number"
          disabled={!clearChoice}
          onChange={(_ev, newValue) => {
            if (newValue) {
              setSeed(parseInt(newValue));
            }
          }}
        />

        <DialogFooter>
          <PrimaryButton
            disabled={!csvValidation?.startsWith('ok')}
            onClick={() => handleImportFileClick()}
            text="Import"
          />
          <DefaultButton
            onClick={() => {
              setIsImportFileDialogOpen(false);
              setCsvValidation(undefined);
            }}
            text="No"
          />
        </DialogFooter>
      </Dialog>

      <Stack tokens={stackTokens}>
        {message ? <div>{message}</div> : ''}
        <Separator alignContent="start">
          <Text variant="large">Last import</Text>
        </Separator>
        <RenderIf condition={!!importData}>
          <Stack horizontal>
            <Text>Import date: </Text>
            <Text>
              {importData?.lastImport?.createdAt
                ? formatDate(importData.lastImport.createdAt, true)
                : ''}
            </Text>
          </Stack>
          <Stack horizontal>
            <Text>Code of latest imported asset: </Text>
            <Text>
              {importData ? importData.latestImportedAsset?.code : ''}
            </Text>
          </Stack>
          <Stack horizontal>
            <Text>Date of latest imported asset: </Text>
            <Text>
              {importData?.latestImportedAsset?.changeDate
                ? formatDate(importData.latestImportedAsset.changeDate, true)
                : ''}
            </Text>
          </Stack>
        </RenderIf>
        {!importData ? (
          <Text>No previous import</Text>
        ) : (
          <>
            <Separator alignContent="start">
              <Text variant="large">Source</Text>
            </Separator>
            <Stack horizontal>
              <Text>Latest asset date: </Text>
              <Text>
                {importData?.latestAssetInSourceDate
                  ? formatDate(importData.latestAssetInSourceDate, true)
                  : ''}
              </Text>
            </Stack>
            <Stack horizontal>
              <Text>Latest asset code: </Text>
              <Text>{importData ? importData.latestAssetInSourceNum : ''}</Text>
            </Stack>
          </>
        )}
        <Separator alignContent="start">
          <Text variant="large">Import logs</Text>
        </Separator>
        <CommandBar items={commandBarItems}></CommandBar>
        <ImportLogs></ImportLogs>
        {showImportedDataList ? (
          <div className={classNames.dataListContainer}>
            <ShimmeredDetailsList
              items={importedAssets}
              columns={columns}
              setKey="set"
              layoutMode={DetailsListLayoutMode.justified}
              selectionPreservedOnEmptyClick={true}
              ariaLabelForSelectionColumn="Toggle selection"
              ariaLabelForSelectAllCheckbox="Toggle selection for all items"
              checkButtonAriaLabel="select row"
              enableShimmer={shimmered}
            />
          </div>
        ) : (
          ''
        )}
      </Stack>
    </>
  );
};

export default ImportAsset;
